import { useEffect, useState } from "react";
import { Briefcase, IndianRupee, Users, Calendar, AlertCircle, TrendingUp, Building2, Award } from "lucide-react";
import { api } from "@/lib/api";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminKPI() {
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/admin/kpi").then((r) => setK(r.data)); }, []);
  if (!k) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-kpi">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Business KPIs</h1>
        <p className="text-sm text-slate-500">Live view of pipeline health, revenue, and recruiter performance</p>
      </div>

      {/* Top KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card icon={IndianRupee} label="Revenue (MTD)" value={inr(k.revenue_mtd)} tone="primary" />
        <Card icon={Award} label="Joined this month" value={k.placements_this_month} tone="secondary" />
        <Card icon={Users} label="Total applications" value={k.total_applications} sub={`+${k.this_month_apps} this month`} />
        <Card icon={Briefcase} label="Active mandates" value={k.active_jobs} sub={`${k.clients_count} active clients`} />
      </div>

      {/* Second row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card icon={Calendar} label="Interviews (next 7d)" value={k.upcoming_interviews} />
        <Card icon={AlertCircle} label="Aging pipeline (>7d)" value={k.aging_pipeline} tone="warn" />
        <Card icon={TrendingUp} label="Offer→Join ratio (90d)" value={`${k.offer_join_ratio}%`} tone="emerald" />
        <Card icon={Building2} label="Total clients" value={k.clients_count} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pipeline stages */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-4 text-sm font-semibold uppercase tracking-widest text-slate-500">Pipeline by stage</div>
          <div className="space-y-3">
            {(k.pipeline_stages || []).map((p) => {
              const max = Math.max(...k.pipeline_stages.map((x) => x.count));
              const pct = max > 0 ? (p.count / max) * 100 : 0;
              return (
                <div key={p._id}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="capitalize text-slate-700">{p._id?.replace("_", " ") || "unknown"}</span>
                    <span className="font-semibold text-slate-900">{p.count}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))" }} />
                  </div>
                </div>
              );
            })}
            {(k.pipeline_stages || []).length === 0 && <div className="text-sm text-slate-400">No data yet.</div>}
          </div>
        </div>

        {/* Recruiter leaderboard */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-4 text-sm font-semibold uppercase tracking-widest text-slate-500">Recruiter leaderboard (all-time)</div>
          <div className="space-y-2">
            {(k.recruiter_leaderboard || []).map((r, i) => (
              <div key={r._id || i} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 font-heading text-sm font-bold text-primary">{i + 1}</div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{r._id || "Unassigned"}</div>
                  <div className="text-xs text-slate-500">{r.count} placement{r.count === 1 ? "" : "s"}</div>
                </div>
                <div className="font-heading font-bold text-secondary">{inr(r.revenue)}</div>
              </div>
            ))}
            {(k.recruiter_leaderboard || []).length === 0 && <div className="text-sm text-slate-400">No placements yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, tone = "default" }) {
  const toneClass = {
    primary: "border-primary/30 bg-primary/5",
    secondary: "border-secondary/30 bg-secondary/5",
    warn: "border-amber-200 bg-amber-50/50",
    emerald: "border-emerald-200 bg-emerald-50/50",
    default: "border-slate-200 bg-white",
  }[tone];
  const iconClass = { primary: "text-primary", secondary: "text-secondary", warn: "text-amber-700", emerald: "text-emerald-700", default: "text-slate-600" }[tone];
  return (
    <div className={`rounded-xl border p-5 ${toneClass}`} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
        <Icon className={`h-4 w-4 ${iconClass}`} />{label}
      </div>
      <div className="mt-2 font-heading text-3xl font-bold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
